import { BrandStatusBadge } from '@/components/BrandChip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { Brand, BrandReviewStatus } from '@rock/types';
import { BrandReviewStatusSchema } from '@rock/types';
import type { FC } from 'react';
import { BRAND_REVIEW_STATUS_META } from './brandReviewStatus';

interface BrandReviewStatusControlProps {
    brand: Brand;
    onChange: (status: BrandReviewStatus) => Promise<void>;
    className?: string;
}

/**
 * Header-level control for the brand's manually-controlled curation status — shown next to the brand
 * name so it stays visible across the Details/Aliases/Models tabs, since curation isn't specific to any
 * one tab. A merged brand's status is the merge's to own (the tombstone's `junk`), so it reads as a
 * "Merged" badge rather than a control.
 */
export const BrandReviewStatusControl: FC<BrandReviewStatusControlProps> = ({ brand, onChange, className }) => {
    const status = brand.reviewStatus;
    const meta = BRAND_REVIEW_STATUS_META[status];

    return (
        <div className={cn('flex flex-col items-end gap-1', className)}>
            {brand.mergedInto ? (
                <BrandStatusBadge brand={brand} className="h-9 px-3 text-sm" />
            ) : (
                <Select value={status} onValueChange={(value) => void onChange(BrandReviewStatusSchema.parse(value))}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue>
                            <span className="flex items-center gap-2">
                                <span className={`h-2 w-2 rounded-full ${meta.dotClassName}`} />
                                {meta.label}
                            </span>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent position="popper" align="end" className="max-w-sm">
                        {Object.entries(BRAND_REVIEW_STATUS_META).map(([value, itemMeta]) => (
                            <SelectItem key={value} value={value}>
                                <span className="flex flex-col gap-0.5">
                                    <span className="flex items-center gap-2">
                                        <span className={`h-2 w-2 rounded-full ${itemMeta.dotClassName}`} />
                                        {itemMeta.label}
                                    </span>
                                    <span className="pl-4 text-xs text-muted-foreground">{itemMeta.hint}</span>
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            {brand.statusChangedAt ? (
                <span className="text-xs text-muted-foreground">
                    Changed {new Date(brand.statusChangedAt).toLocaleDateString()}
                </span>
            ) : null}
        </div>
    );
};
